import { Form } from "@unform/web";
import { Building, ChevronDown, ChevronRight, Pencil, Trash, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { Zoom, toast } from "react-toastify";
import api from "~/services/api";
import { getRegistries } from "~/functions";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";

export const InputContext = createContext({});

function DescriptionRow({ desc, descIndex, expanded, onToggle, onRemove, onAddOption, onRemoveOption }) {
  const optionInputRef = useRef();

  function handleOptionKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      const val = optionInputRef.current.value.trim();
      if (val) {
        onAddOption(descIndex, val);
        optionInputRef.current.value = "";
      }
    }
  }

  function handleOptionAdd() {
    const val = optionInputRef.current.value.trim();
    if (val) {
      onAddOption(descIndex, val);
      optionInputRef.current.value = "";
    }
    optionInputRef.current.focus();
  }

  return (
    <div className="flex flex-col border-b border-gray-200 last:border-b-0">
      <div
        className={`flex flex-row items-center justify-between px-3 py-2 cursor-pointer select-none transition-colors ${expanded ? "bg-indigo-50 border-l-2 border-l-mila_orange" : "hover:bg-gray-100"}`}
        onClick={onToggle}
      >
        <div className="flex flex-row items-center gap-2">
          {expanded
            ? <ChevronDown size={13} className="text-mila_orange" />
            : <ChevronRight size={13} className="text-gray-500" />}
          <span className={`text-xs font-medium ${expanded ? "text-mila_orange" : "text-gray-700"}`}>{desc.name}</span>
          <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-1.5 py-0.5">{desc.options.length}</span>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(descIndex); }}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash size={12} />
        </button>
      </div>

      {expanded && (
        <div className="bg-white border-t border-gray-200 ml-4 mr-2 my-2 rounded-md border border-gray-200 overflow-hidden">
          <div className="px-3 py-1.5 bg-gray-100 border-b border-gray-200">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Options</span>
          </div>
          {desc.options.length === 0 && (
            <div className="text-xs text-gray-400 px-3 py-2 text-center">No options yet.</div>
          )}
          {desc.options.map((opt, optIndex) => (
            <div key={optIndex} className="flex flex-row items-center justify-between px-3 py-1.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
              <span className="text-xs text-gray-700">{opt.name}</span>
              <button
                type="button"
                onClick={() => onRemoveOption(descIndex, optIndex)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash size={11} />
              </button>
            </div>
          ))}
          <div className="flex flex-row items-center gap-1 px-2 py-1.5 bg-gray-50 border-t border-dashed border-gray-300">
            <input
              ref={optionInputRef}
              type="text"
              placeholder="Add option and press Enter..."
              className="flex-1 text-xs bg-transparent focus:outline-none placeholder-gray-400 text-gray-700"
              onKeyDown={handleOptionKeyDown}
            />
            <button
              type="button"
              onClick={handleOptionAdd}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-mila_orange text-white text-xs font-bold hover:opacity-80 transition-opacity flex-shrink-0"
            >
              +
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);

  const [pageData, setPageData] = useState({ name: "", loaded: false });
  const [registry, setRegistry] = useState({
    created_by: null, created_at: null,
    updated_by: null, updated_at: null,
    canceled_by: null, canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [descriptions, setDescriptions] = useState([]);
  const [expandedDescIndex, setExpandedDescIndex] = useState(null);
  const [deletedDescriptions, setDeletedDescriptions] = useState([]);
  const [deletedOptions, setDeletedOptions] = useState([]);

  const generalForm = useRef();
  const descInputRef = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) toast("Changes discarted!", { autoClose: 1000 });
    handleOpened(null);
  }

  function handleDescAdd() {
    const val = descInputRef.current.value.trim();
    if (val) {
      setDescriptions((prev) => [...prev, { name: val, options: [] }]);
      descInputRef.current.value = "";
      setSuccessfullyUpdated(false);
    }
    descInputRef.current.focus();
  }

  function handleDescKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleDescAdd();
    }
  }

  function handleRemoveDescription(index) {
    setDescriptions((prev) => {
      const removed = prev[index];
      if (removed?.id) setDeletedDescriptions((ids) => [...ids, removed.id]);
      return prev.filter((_, i) => i !== index);
    });
    if (expandedDescIndex === index) setExpandedDescIndex(null);
    setSuccessfullyUpdated(false);
  }

  function handleAddOption(descIndex, optName) {
    setDescriptions((prev) =>
      prev.map((desc, i) =>
        i !== descIndex ? desc : { ...desc, options: [...desc.options, { name: optName }] }
      )
    );
    setSuccessfullyUpdated(false);
  }

  function handleRemoveOption(descIndex, optIndex) {
    setDescriptions((prev) =>
      prev.map((desc, i) => {
        if (i !== descIndex) return desc;
        const removed = desc.options[optIndex];
        if (removed?.id) setDeletedOptions((ids) => [...ids, removed.id]);
        return { ...desc, options: desc.options.filter((_, oi) => oi !== optIndex) };
      })
    );
    setSuccessfullyUpdated(false);
  }

  async function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", { autoClose: 1000, type: "info", transition: Zoom });
      return;
    }

    const payload = {
      name: data.name,
      descriptions,
      deleted_descriptions: deletedDescriptions,
      deleted_options: deletedOptions,
    };

    if (id === "new") {
      try {
        const response = await api.post(`/inactivation-reasons`, payload);
        setOpened(response.data.id);
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    } else {
      try {
        await api.put(`/inactivation-reasons/${id}`, payload);
        setPageData({ ...pageData, ...data });
        setDeletedDescriptions([]);
        setDeletedOptions([]);
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
  }

  useEffect(() => {
    async function getPageData() {
      try {
        const { data } = await api.get(`inactivation-reasons/${id}`);
        setPageData({ ...data, loaded: true });
        setDescriptions(data.descriptions || []);
        const { created_by, created_at, updated_by, updated_at, canceled_by, canceled_at } = data;
        const registries = await getRegistries({ created_by, created_at, updated_by, updated_at, canceled_by, canceled_at });
        setRegistry(registries);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
    if (id === "new") {
      setFormType("full");
    } else if (id) {
      getPageData();
    }
  }, []);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <Building size={16} /> General
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                    }}
                  >
                    {id === "new" || pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={pageData.name || "New Inactivation Reason"}
                          registry={registry}
                          InputContext={InputContext}
                        />

                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          <InputLine title="General Data">
                            <Input
                              type="text"
                              name="name"
                              required
                              title="Name"
                              grow
                              defaultValue={pageData.name}
                              InputContext={InputContext}
                            />
                          </InputLine>

                          {/* Descriptions */}
                          <div className="w-full px-4 pb-4 flex flex-col gap-2">
                            <div className="py-2 border-b border-gray-200">
                              <span className="font-semibold text-xs text-gray-500 uppercase tracking-wide">
                                Descriptions
                              </span>
                            </div>

                            <div className="border border-gray-200 rounded-md overflow-hidden">
                              {descriptions.length === 0 && (
                                <div className="text-xs text-gray-400 px-3 py-2 text-center">
                                  No descriptions yet. Type below to add one.
                                </div>
                              )}
                              {descriptions.map((desc, descIndex) => (
                                <DescriptionRow
                                  key={descIndex}
                                  desc={desc}
                                  descIndex={descIndex}
                                  expanded={expandedDescIndex === descIndex}
                                  onToggle={() => setExpandedDescIndex(expandedDescIndex === descIndex ? null : descIndex)}
                                  onRemove={handleRemoveDescription}
                                  onAddOption={handleAddOption}
                                  onRemoveOption={handleRemoveOption}
                                />
                              ))}
                              <div className="flex flex-row items-center gap-1 px-3 py-2 bg-gray-50 border-t border-dashed border-gray-300">
                                <input
                                  ref={descInputRef}
                                  type="text"
                                  placeholder="Add description and press Enter..."
                                  className="flex-1 text-xs bg-transparent focus:outline-none placeholder-gray-400 text-gray-700"
                                  onKeyDown={handleDescKeyDown}
                                />
                                <button
                                  type="button"
                                  onClick={handleDescAdd}
                                  className="w-5 h-5 flex items-center justify-center rounded-full bg-mila_orange text-white text-xs font-bold hover:opacity-80 transition-opacity flex-shrink-0"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
